import { useEffect, useState, useCallback, useRef, useReducer } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Chart, { ChartHandle } from '../components/chart/Chart';
import ChartToolbar from '../components/chart/ChartToolbar';
import AnalysisPanel from '../components/analysis/AnalysisPanel';
import AnalysisHistoryPanel from '../components/analysis/AnalysisHistoryPanel';
import ScanningAnimation from '../components/analysis/ScanningAnimation';
import PanelToggle from '../components/analysis/PanelToggle';
import UpgradePrompt from '../components/ui/UpgradePrompt';
import { useShallow } from 'zustand/react/shallow';
import { useAnalysisStore } from '../store/analysisStore';
import { marketService } from '../services/market.service';
import { analysisService } from '../services/analysis.service';
import { useTickStream } from '../hooks/useTickStream';
import { ANALYSIS_CANDLE_MIN, ANALYSIS_CANDLE_MAX } from '@trading/shared';
import type { MarketProvider } from '@trading/shared';
import { historyReducer, HIGH_WATER_BATCHES, TARGET_BUFFERED_BATCHES, PIPELINE_BACKOFF_MS } from './analysisHistoryState';
import { shouldAutoExpandPanel } from './analysisAutoExpand';

const ANALYSIS_TIMEOUT_MS = 90000; // 90 seconds timeout

const HISTORY_BATCH_SIZE_SLOW = 50;
const HISTORY_BATCH_SIZE_FAST = 200;
const VELOCITY_THRESHOLD = 20; // bars/sec

export default function Analysis() {
  const { t } = useTranslation('analysis');
  const {
    symbol,
    timeframe,
    methodology,
    aiModel,
    candles,
    setCandles,
    selectedUserPromptId,
    debugProvider,
    currentAnalysis,
    isAnalyzing,
    analysisProgress,
    startAnalysis,
    setAnalysisProgress,
    setAnalysisResult,
    setAnalysisError,
    clearAnalysis,
    loadAnalysis,
  } = useAnalysisStore(
    useShallow((s) => ({
      symbol: s.symbol,
      timeframe: s.timeframe,
      methodology: s.methodology,
      aiModel: s.aiModel,
      candles: s.candles,
      setCandles: s.setCandles,
      selectedUserPromptId: s.selectedUserPromptId,
      debugProvider: s.debugProvider,
      currentAnalysis: s.currentAnalysis,
      isAnalyzing: s.isAnalyzing,
      analysisProgress: s.analysisProgress,
      startAnalysis: s.startAnalysis,
      setAnalysisProgress: s.setAnalysisProgress,
      setAnalysisResult: s.setAnalysisResult,
      setAnalysisError: s.setAnalysisError,
      clearAnalysis: s.clearAnalysis,
      loadAnalysis: s.loadAnalysis,
    }))
  );

  const [loading, setLoading] = useState(false);
  const [lastPrice, setLastPrice] = useState<number | null>(null);
  const [currentAnalysisId, setCurrentAnalysisId] = useState<string | null>(null);
  const [analysisTimedOut, setAnalysisTimedOut] = useState(false);
  const [showSMC, setShowSMC] = useState(true);
  const [feedUnavailable, setFeedUnavailable] = useState(false);
  // Provider that served the currently-rendered candles. The tick handler
  // gates incoming ticks against this — only ticks emitted by the same
  // provider may reshape the live bar (open/close/high/low). Without this
  // gate, e.g. TwelveData ticks would corrupt OANDA-sourced candles.
  const [activeCandleSource, setActiveCandleSource] = useState<MarketProvider | null>(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [panelOpen, setPanelOpen] = useState(false);
  const chartRef = useRef<ChartHandle>(null);
  const toastShownRef = useRef<string | null>(null); // Prevent duplicate toasts
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // History state machine
  const [historyState, historyDispatch] = useReducer(historyReducer, {
    kind: 'idle', oldestTime: 0, buffer: [],
  });
  const historyStateRef = useRef(historyState);
  historyStateRef.current = historyState; // always current
  const historyAbortRef = useRef<AbortController | null>(null);
  const panHistoryRef = useRef<Array<{ time: number; from: number }>>([]);
  // Timestamp of the last FETCH_ERROR — used by the pipeline-fill effect to
  // enforce a 5-second backoff so a persistent network failure does not
  // create a tight retry loop.
  const lastFetchErrorRef = useRef<number>(0);

  // Clear timeout on unmount or when analysis completes
  const clearAnalysisTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // Handle analysis result (shared between WebSocket update and status check)
  const handleAnalysisComplete = useCallback((analysisId: string, result: Parameters<typeof setAnalysisResult>[0]) => {
    // Prevent duplicate processing
    if (toastShownRef.current === analysisId) return;
    toastShownRef.current = analysisId;

    clearAnalysisTimeout();
    setAnalysisTimedOut(false);
    setAnalysisResult(result);
    toast.success(t('completed'));
    setCurrentAnalysisId(null);
  }, [clearAnalysisTimeout, setAnalysisResult, t]);

  // Handle analysis failure
  const handleAnalysisFailure = useCallback((message: string) => {
    clearAnalysisTimeout();
    setAnalysisTimedOut(false);
    setAnalysisError(message);
    // Only show toast if we have a current analysis (avoid duplicate toasts on reconnection)
    if (currentAnalysisId) {
      toast.error(message);
    }
    setCurrentAnalysisId(null);
  }, [clearAnalysisTimeout, setAnalysisError, currentAnalysisId]);

  // Real-time tick stream and analysis updates
  const { connected, subscribe, unsubscribe, checkAnalysis } = useTickStream({
    onTick: (tick) => {
      if (tick.symbol !== symbol) return;
      // Source-match invariant: a tick may only update the chart when its
      // emitting provider matches the provider that served the candles.
      // Mixing sources (e.g. TwelveData ticks on OANDA candles) reshapes
      // bars with prices from a different feed and miscolors them. Both
      // sides are stamped at the data boundary — `tick.source` by the
      // backend publisher, `activeCandleSource` by the candles route.
      if (!tick.source || !activeCandleSource) return;
      if (tick.source !== activeCandleSource) return;
      setLastPrice(tick.price);
      chartRef.current?.updateWithTick(tick);
    },
    onAnalysisUpdate: (update) => {
      // Only process updates for current analysis
      if (update.analysisId !== currentAnalysisId) return;

      if (update.type === 'processing' && update.progress) {
        setAnalysisProgress(update.progress);
      }

      if (update.type === 'completed' && update.result) {
        const { setup, analysis, structure, zones, narrative, chartMarkers } = update.result;
        handleAnalysisComplete(update.analysisId, {
          id: update.analysisId,
          userId: '',
          symbol,
          timeframe,
          methodology,
          status: 'completed',
          direction: setup.direction?.toLowerCase() as 'long' | 'short' | null,
          confidence: setup.confidence,
          entry: setup.entry?.price,
          stopLoss: setup.stopLoss?.price,
          riskReward: setup.riskReward,
          durationMs: update.durationMs,
          aiModel: update.aiModel,
          analysis: analysis as import('@trading/shared').AnalysisResult['analysis'],
          structure: structure as import('@trading/shared').AnalysisResult['structure'],
          zones: zones as import('@trading/shared').AnalysisResult['zones'],
          setup: {
            valid: setup.valid,
            direction: setup.direction,
            confidence: setup.confidence,
            entry: setup.entry ? {
              price: setup.entry.price,
              type: setup.entry.type as 'limit' | 'market' | 'stop',
              reasoning: setup.entry.reasoning,
            } : null,
            stopLoss: setup.stopLoss || null,
            targets: setup.targets || null,
            riskReward: setup.riskReward || null,
            riskPercent: setup.riskPercent || null,
            invalidation: setup.invalidation || null,
          },
          narrative,
          chartMarkers: (chartMarkers || []) as import('@trading/shared').ChartMarker[],
          createdAt: new Date(),
        });
      }

      if (update.type === 'error') {
        handleAnalysisFailure(update.error || t('error.analysisFailed'));
      }
    },
    onAnalysisStatus: (response) => {
      // Handle status check response (from reconnection)
      if (response.analysisId !== currentAnalysisId) return;

      if ((response.status === 'completed' || response.status === 'no_setup') && response.result) {
        const { setup, analysis, structure, zones, narrative, chartMarkers } = response.result;
        handleAnalysisComplete(response.analysisId, {
          id: response.analysisId,
          userId: '',
          symbol,
          timeframe,
          methodology,
          status: response.status,
          direction: setup.direction?.toLowerCase() as 'long' | 'short' | null,
          confidence: setup.confidence,
          entry: setup.entry?.price,
          stopLoss: setup.stopLoss?.price,
          riskReward: setup.riskReward,
          analysis: analysis as import('@trading/shared').AnalysisResult['analysis'],
          structure: structure as import('@trading/shared').AnalysisResult['structure'],
          zones: zones as import('@trading/shared').AnalysisResult['zones'],
          setup: {
            valid: setup.valid,
            direction: setup.direction,
            confidence: setup.confidence,
            entry: setup.entry ? {
              price: setup.entry.price,
              type: setup.entry.type as 'limit' | 'market' | 'stop',
              reasoning: setup.entry.reasoning,
            } : null,
            stopLoss: setup.stopLoss || null,
            targets: setup.targets || null,
            riskReward: setup.riskReward || null,
            riskPercent: setup.riskPercent || null,
            invalidation: setup.invalidation || null,
          },
          narrative,
          chartMarkers: (chartMarkers || []) as import('@trading/shared').ChartMarker[],
          createdAt: new Date(),
        });
      } else if (response.status === 'failed' || response.status === 'stale') {
        handleAnalysisFailure(response.message || t('error.analysisTimedOut'));
      } else if (response.status === 'not_found' || response.status === 'error') {
        handleAnalysisFailure(t('error.notFound'));
      }
      // If still 'pending', keep waiting (WebSocket will deliver result)
    },
    onReconnect: () => {
      // On reconnection, check if we have a pending analysis
      if (currentAnalysisId && isAnalyzing) {
        checkAnalysis(currentAnalysisId);
      }
    },
  });

  // Subscribe to symbol when it changes
  useEffect(() => {
    if (symbol) {
      subscribe(symbol);
      return () => unsubscribe(symbol);
    }
  }, [symbol, subscribe, unsubscribe]);

  // Polling fallback: if WebSocket misses the result, poll the API every 3s
  // Stops automatically when analysis completes (via handleAnalysisComplete setting currentAnalysisId to null)
  useEffect(() => {
    if (!currentAnalysisId || !isAnalyzing) return;

    const pollInterval = setInterval(async () => {
      try {
        const analysis = await analysisService.getById(currentAnalysisId);
        if ((analysis.status === 'completed' || analysis.status === 'no_setup') && analysis.setup) {
          // Polling returns the API's StoredAnalysisPayload shape (raw DB
          // direction strings). Funnel through the store's loadAnalysis
          // which normalises direction casing into AnalysisResult, then
          // run the same end-of-flow side effects as the WebSocket path.
          if (toastShownRef.current === currentAnalysisId) return;
          toastShownRef.current = currentAnalysisId;
          loadAnalysis(analysis);
          clearAnalysisTimeout();
          setAnalysisTimedOut(false);
          toast.success(t('completed'));
          setCurrentAnalysisId(null);
        } else if (analysis.status === 'failed') {
          handleAnalysisFailure(analysis.narrative || t('error.analysisFailed'));
        }
      } catch (err) {
        console.warn('[Analysis] Polling error:', err);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [currentAnalysisId, isAnalyzing, loadAnalysis, clearAnalysisTimeout, handleAnalysisFailure, t]);

  // Aborts the prior in-flight request when symbol/timeframe changes so a
  // late-arriving short payload from the previous selection cannot overwrite
  // the fresh data — that race is what produced the "1 candle on chart"
  // state where Analyse was still allowed to fire.
  const loadCandlesAbortRef = useRef<AbortController | null>(null);
  // Last (symbol, timeframe) the chart was loaded against. When loadCandles
  // re-runs with the same pair, the trigger was a provider switch — preserve
  // the user's current visible range instead of snapping back to default.
  const lastLoadedKeyRef = useRef<string | null>(null);

  // Fetches up to `limit` candles before `beforeTime`, skipping
  // weekend/gap holes by stepping back up to 3 days when the primary request
  // returns empty.
  const fetchHistoryBatch = useCallback(
    async (beforeTime: number, signal: AbortSignal, limit = HISTORY_BATCH_SIZE_SLOW) => {
      let result = await marketService.getCandles(
        symbol, timeframe, limit, beforeTime, signal, debugProvider ?? undefined,
      );
      if (result.candles.length === 0 && !signal.aborted) {
        for (let skipDays = 1; skipDays <= 3; skipDays++) {
          if (signal.aborted) break;
          result = await marketService.getCandles(
            symbol, timeframe, limit, beforeTime - skipDays * 86400, signal, debugProvider ?? undefined,
          );
          if (result.candles.length > 0) break;
        }
      }
      return result.candles;
    },
    [symbol, timeframe, debugProvider],
  );

  // Start a single background history fetch — transitions idle → fetching.
  // Caps at HIGH_WATER_BATCHES to avoid unbounded prefetch.
  const startHistoryFetch = useCallback((batchSize: number) => {
    const state = historyStateRef.current;
    if (state.kind !== 'idle') return;
    if (state.oldestTime === 0) return;
    if (state.buffer.length >= HIGH_WATER_BATCHES) return;

    historyAbortRef.current?.abort();
    const controller = new AbortController();
    historyAbortRef.current = controller;

    // Optimistic update: mirror the FETCH_START transition in the ref so that
    // a second caller (pipeline-fill useEffect or handleVisibleRangeChange) that
    // runs before React commits the dispatch cannot also pass the kind==='idle'
    // guard and issue a second parallel fetch.  The ref is overwritten on the
    // next render once the reducer has processed FETCH_START.
    historyStateRef.current = { ...state, kind: 'fetching' };
    historyDispatch({ type: 'FETCH_START' });

    fetchHistoryBatch(state.oldestTime, controller.signal, batchSize)
      .then(batch => {
        if (controller.signal.aborted) return;
        if (batch.length === 0) {
          historyDispatch({ type: 'EXHAUSTED' });
        } else {
          historyDispatch({ type: 'BATCH_APPENDED', batch, newOldestTime: batch[0].time });
        }
      })
      .catch(() => {
        if (controller.signal.aborted) return; // intentional abort — RESET will follow on next load
        lastFetchErrorRef.current = Date.now(); // record error time for pipeline-fill backoff
        historyDispatch({ type: 'FETCH_ERROR' }); // network/timeout — return to idle for retry
      });
  }, [fetchHistoryBatch]);

  // Pipeline fill: whenever the state machine goes idle with the buffer below
  // target, automatically kick off another background fetch.
  // Backoff: skip the fetch if a FETCH_ERROR occurred in the last 5 seconds —
  // without this guard a persistent network failure creates a tight retry loop
  // (~60 req/s) because FETCH_ERROR transitions to idle, which re-fires this
  // effect immediately.
  // No cleanup-abort here: the next render (kind='fetching') would run the
  // cleanup and abort our own just-started fetch before it resolves, leaving
  // the state stuck in 'fetching' (the abort path bails without dispatching).
  // loadCandles and startHistoryFetch already abort the prior controller.
  useEffect(() => {
    if (historyState.kind !== 'idle') return;
    if (historyState.oldestTime === 0) return;
    if (historyState.buffer.length >= TARGET_BUFFERED_BATCHES) return;
    if (Date.now() - lastFetchErrorRef.current < PIPELINE_BACKOFF_MS) return;
    startHistoryFetch(HISTORY_BATCH_SIZE_SLOW);
  }, [historyState, startHistoryFetch]);

  // Load candles when symbol or timeframe changes.
  // Tuned per timeframe so SMC has enough context (BOS/CHoCH ~50-80, order
  // blocks ~30-50, FVG ~20-30, trend ~100) without ballooning the payload
  // and Gemini token count — the bigger the array, the slower the analysis.
  // Always above the API's 50-candle minimum.
  const loadCandles = useCallback(async () => {
    loadCandlesAbortRef.current?.abort();
    historyAbortRef.current?.abort();
    // Reset state machine — the chaining effect will start priming automatically
    historyDispatch({ type: 'RESET', oldestTime: 0 });
    panHistoryRef.current = [];

    const controller = new AbortController();
    loadCandlesAbortRef.current = controller;

    setLoading(true);
    setFeedUnavailable(false);
    try {
      // Big initial window so the user can pan years of history without
      // burning the per-minute TD quota on incremental backfill. 5000 is
      // TwelveData's outputsize cap and matches the candles route limit.
      // Coverage at 5000 bars: 1m ≈ 3.5 days · 5m ≈ 17 days · 15m ≈ 52 days
      // 30m ≈ 104 days · 1h ≈ 208 days · 4h ≈ 2.3 years · 1d ≈ 13.7 years.
      // Analysis-bound candles are sliced to ANALYSIS_CANDLE_MAX before send
      // (the AI pipeline doesn't benefit from extra historical context and
      // billing scales with prompt tokens).
      const candleCount = 5000;
      const result = await marketService.getCandles(symbol, timeframe, candleCount, undefined, controller.signal, debugProvider ?? undefined);
      if (controller.signal.aborted) return;
      const { candles: data, resolvedProvider } = result;
      setCandles(data);
      // Record the actual provider that served these candles. The tick
      // handler matches incoming ticks against this so live updates only
      // apply when the tick stream's source equals the candle source.
      setActiveCandleSource(resolvedProvider);
      const loadKey = `${symbol}|${timeframe}`;
      const isProviderOnlySwitch = lastLoadedKeyRef.current === loadKey;
      chartRef.current?.setInitialCandles(data, { preserveVisibleRange: isProviderOnlySwitch });
      lastLoadedKeyRef.current = loadKey;
      // Prime the buffer: dispatch RESET with anchor; the chaining effect will fill it
      if (data.length > 0) {
        historyDispatch({ type: 'RESET', oldestTime: data[0].time });
      }
    } catch (error: unknown) {
      if (controller.signal.aborted) return;
      // Check if it's a feed unavailable error
      const err = error as { response?: { data?: { error?: string; message?: string } } };
      if (err?.response?.data?.error === 'FEED_UNAVAILABLE') {
        setFeedUnavailable(true);
        setCandles([]);
      } else {
        toast.error(t('error.loadMarketData'));
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [symbol, timeframe, setCandles, t, debugProvider]);

  useEffect(() => {
    loadCandles();
    return () => {
      loadCandlesAbortRef.current?.abort();
      historyAbortRef.current?.abort();
    };
  }, [loadCandles]);

  // Handle visible range changes from Chart — implements low-water-mark trigger,
  // buffer flush, and velocity-aware batch sizing.
  // NOTE: Chart.tsx debounces this callback to 200 ms (RANGE_CHANGE_DEBOUNCE_MS), so
  // panHistoryRef receives at most one sample per gesture.  With a single sample the
  // velocity window always yields barsPerSec = 0, making HISTORY_BATCH_SIZE_FAST
  // unreachable during debounced scrolls.  This is an accepted trade-off: the
  // 10–30x reduction in request volume outweighs the occasional slow-batch cost.
  const handleVisibleRangeChange = useCallback((range: { from: number; to: number }) => {
    // Velocity tracking (last 1 second of pan samples)
    const now = Date.now();
    const history = panHistoryRef.current;
    history.push({ time: now, from: range.from });
    const cutoff = now - 1000;
    while (history.length > 0 && history[0].time < cutoff) history.shift();

    const state = historyStateRef.current;
    if (state.kind === 'exhausted') return;

    const visibleWindow = Math.max(1, range.to - range.from);
    const LOW_WATER = Math.ceil(2 * visibleWindow);
    const totalBuffered = state.buffer.reduce((s, b) => s + b.length, 0);
    // `range.from` is the logical bar index of the left edge of the visible window
    // (i.e. how many rendered bars sit to the left of the viewport).
    // Adding buffered candle count gives the total "runway" before we'd hit empty space.
    const barsAhead = Math.max(0, Math.floor(range.from)) + totalBuffered;

    if (barsAhead >= LOW_WATER) return;

    // Flush a buffered batch if available
    if (state.buffer.length > 0) {
      const batch = state.buffer[0];
      // Optimistic update to prevent double-flush before re-render
      historyStateRef.current = { ...state, buffer: state.buffer.slice(1) };
      historyDispatch({ type: 'CONSUME' });
      chartRef.current?.prependCandles(batch);
      return;
    }

    // Buffer empty — start fetching with velocity-aware batch size
    if (state.kind === 'idle') {
      const h = panHistoryRef.current;
      let batchSize = HISTORY_BATCH_SIZE_SLOW;
      if (h.length >= 2) {
        const dt = (h[h.length - 1].time - h[0].time) / 1000;
        if (dt > 0) {
          const barsPerSec = Math.abs(h[h.length - 1].from - h[0].from) / dt;
          if (barsPerSec > VELOCITY_THRESHOLD) batchSize = HISTORY_BATCH_SIZE_FAST;
        }
      }
      startHistoryFetch(batchSize);
    }
  }, [startHistoryFetch]);

  // Handle analyze - results come via WebSocket with timeout protection
  const handleAnalyze = async () => {
    // The toolbar already disables the button, but a stale render or a
    // mid-flight symbol/timeframe change can still let `handleAnalyze` fire
    // with too few candles — guard at the call site so we never hit the API
    // with an obviously-bad payload.
    if (candles.length < ANALYSIS_CANDLE_MIN) {
      const msg = candles.length === 0
        ? t('error.notEnoughData')
        : t('error.insufficientCandles', { min: ANALYSIS_CANDLE_MIN, current: candles.length });
      toast.error(msg);
      return;
    }

    if (!connected) {
      toast.error(t('error.notConnected'));
      return;
    }

    // Clear any previous state
    clearAnalysisTimeout();
    setAnalysisTimedOut(false);
    toastShownRef.current = null;

    startAnalysis();

    try {
      // Trim to the most recent ANALYSIS_CANDLE_MAX bars before sending — the
      // chart loads up to 5000 for free panning, but the AI pipeline rejects
      // payloads above this cap and prompt cost scales with array length.
      const analysisCandles = candles.length > ANALYSIS_CANDLE_MAX
        ? candles.slice(-ANALYSIS_CANDLE_MAX)
        : candles;
      // Create analysis request - result will come via WebSocket
      const { id } = await analysisService.create({
        symbol,
        timeframe,
        methodology,
        aiModel,
        candles: analysisCandles,
        userPromptId: selectedUserPromptId ?? undefined,
      });

      setCurrentAnalysisId(id);

      // Set timeout for analysis
      timeoutRef.current = setTimeout(() => {
        if (isAnalyzing) {
          setAnalysisTimedOut(true);
          setAnalysisError(t('error.takingLonger'));
        }
      }, ANALYSIS_TIMEOUT_MS);

    } catch (error: unknown) {
      const axiosError = error as {
        response?: {
          status?: number;
          data?: {
            message?: string;
            code?: string;
            error?: {
              code?: string;
              message?: string;
              details?: {
                _root?: unknown;
                candles?: unknown | { min?: number; max?: number; current?: number };
              };
            };
          };
        };
      };
      const status = axiosError?.response?.status;
      // The API error envelope nests under `error.code`; fall back to flat
      // `data.code` for older/proxied error shapes.
      const code =
        axiosError?.response?.data?.error?.code ??
        axiosError?.response?.data?.code;
      const message =
        axiosError?.response?.data?.error?.message ??
        axiosError?.response?.data?.message;
      const validationDetails = axiosError?.response?.data?.error?.details;

      // Trial expired (402 SUB_008): no inline upgrade prompt — the global
      // TrialExpiredModal will already be visible the moment the auth store
      // reflects the expired subscription. Just stop the spinner with a
      // descriptive error and let the modal take over.
      if (status === 402 && code === 'SUB_008') {
        setAnalysisError(message || t('error.trialExpired'));
        return;
      }

      // Validation 400 — most common failure is fewer than the minimum
      // candles (e.g. weekend gap). Backend now sends the actual bound +
      // count in details.candles; fall back to current local count if the
      // payload is from the older generic Zod path.
      if (status === 400 && validationDetails?.candles) {
        const candleErr =
          typeof validationDetails.candles === 'object' && validationDetails.candles
            ? (validationDetails.candles as { min?: number; current?: number })
            : {};
        const min = candleErr.min ?? 50;
        const current = candleErr.current ?? candles.length;
        const msg = t('error.insufficientCandles', { min, current });
        toast.error(msg);
        setAnalysisError(msg);
        return;
      }

      // Show upgrade prompt for plan limit errors
      if (status === 403 && code?.startsWith('SUB_')) {
        setUpgradeMessage(message || t('error.planLimitReached'));
        setShowUpgradePrompt(true);
        setAnalysisError(message || t('error.planLimitReached'));
        return;
      }

      setAnalysisError(error instanceof Error ? error.message : t('error.analysisFailed'));
      toast.error(message || t('error.startFailed'));
    }
  };

  // Handle retry after timeout or failure
  const handleRetry = () => {
    clearAnalysis();
    setAnalysisTimedOut(false);
    setCurrentAnalysisId(null);
    clearAnalysisTimeout();
    // Small delay to ensure state is cleared
    setTimeout(() => handleAnalyze(), 100);
  };

  // Handle cancel analysis
  const handleCancelAnalysis = () => {
    clearAnalysisTimeout();
    clearAnalysis();
    setAnalysisTimedOut(false);
    setCurrentAnalysisId(null);
    toast(t('cancelled'), { icon: '⚠️' });
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => clearAnalysisTimeout();
  }, [clearAnalysisTimeout]);

  // Auto-expand contract:
  //   - isAnalyzing flips on                      → expand
  //   - analysis id changes (new result, history) → expand
  //   - same analysis re-rendered (ref churn)     → no force-open
  //   - user manual collapse stays sticky between identity changes
  // Keying on currentAnalysis?.id rather than the object reference avoids
  // re-opening a panel the user just collapsed when an unrelated re-render
  // produces a fresh currentAnalysis reference with the same identity.
  useEffect(() => {
    if (shouldAutoExpandPanel({ isAnalyzing, analysisId: currentAnalysis?.id })) {
      setPanelOpen(true);
    } else {
      // Empty state — no analysis and nothing in flight. Collapse so the
      // chart isn't shrunk by an empty placeholder panel.
      setPanelOpen(false);
    }
  }, [isAnalyzing, currentAnalysis?.id]);

  // Escape closes the panel — useful on desktop where there is no backdrop
  useEffect(() => {
    if (!panelOpen) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPanelOpen(false);
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [panelOpen]);

  return (
    <div className="flex flex-col gap-4 pb-4">
    <div className="flex flex-col lg:flex-row lg:h-[calc(100vh-130px)] gap-4">
      {/* Chart Area — lg:min-w-0 overrides the default min-width:auto so the
          chart can shrink in flex-row when the analysis panel is a sibling */}
      <div className="h-[60vh] lg:h-full lg:min-h-0 lg:min-w-0 lg:flex-1 flex flex-col bg-bg-secondary rounded-lg border border-border-subtle">
        <ChartToolbar
          onAnalyze={handleAnalyze}
          onRefresh={loadCandles}
          isAnalyzing={isAnalyzing}
          isLoading={loading}
          candleCount={candles.length}
          showSMC={showSMC}
          onToggleSMC={() => setShowSMC(!showSMC)}
          hasAnalysis={!!currentAnalysis}
          onClearAnalysis={clearAnalysis}
          analysisCompleted={currentAnalysis?.id}
        />

        <div className="flex-1 relative min-h-0">
          <Chart
            ref={chartRef}
            markers={currentAnalysis?.chartMarkers}
            tradeSetup={currentAnalysis?.setup}
            smcData={currentAnalysis ? {
              orderBlocks: currentAnalysis.zones?.orderBlocks,
              fvg: currentAnalysis.zones?.fvg,
              liquidity: currentAnalysis.zones?.liquidity,
              lastBos: currentAnalysis.structure?.lastBos,
              lastChoch: currentAnalysis.structure?.lastChoch,
              keyLevels: currentAnalysis.structure?.keyLevels,
            } : undefined}
            methodologyOverlay={currentAnalysis?.methodologyOverlay ?? null}
            showSMC={showSMC}
            analysisTime={currentAnalysis?.createdAt}
            timeframe={timeframe}
            symbol={symbol}
            enableDrawing
            isScanning={isAnalyzing && !analysisTimedOut}
            onVisibleRangeChange={handleVisibleRangeChange}
            isLoadingHistory={historyState.kind === 'fetching'}
            isExhausted={historyState.kind === 'exhausted'}
          />

          {/* Panel toggle — top-right of chart, always visible */}
          <PanelToggle
            open={panelOpen}
            onToggle={() => setPanelOpen((o) => !o)}
            label={panelOpen ? t('panel.hide') : t('panel.show')}
            className="absolute top-3 right-3 z-10"
          />

          {/* Live price indicator — shifted left to clear the panel toggle */}
          {connected && lastPrice && (
            <div className="absolute top-3 right-14 px-3 py-1.5 rounded-lg bg-bg-elevated border border-border-subtle">
              <div className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-profit animate-pulse" />
                <span className="font-mono text-sm text-text-primary">
                  {lastPrice.toFixed(5)}
                </span>
              </div>
            </div>
          )}

          {/* Scanning animation overlay */}
          <ScanningAnimation isActive={isAnalyzing && !analysisTimedOut} progress={analysisProgress} />

          {/* Feed unavailable overlay */}
          {feedUnavailable && (
            <div className="absolute inset-0 bg-bg-primary/95 backdrop-blur-sm flex items-center justify-center z-20">
              <div className="bg-bg-secondary border border-border-default rounded-xl p-8 max-w-md text-center shadow-xl">
                <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-accent-primary/20 flex items-center justify-center">
                  <svg className="w-8 h-8 text-accent-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-text-primary mb-3">
                  {t('feedUnavailable.title')}
                </h3>
                <p className="text-sm text-text-secondary mb-6 leading-relaxed">
                  {t('feedUnavailable.message', { symbol })}
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => {
                      setFeedUnavailable(false);
                      // Switch to a forex symbol
                      const { setSymbol } = useAnalysisStore.getState();
                      setSymbol('frxEURUSD');
                    }}
                    className="px-5 py-2.5 text-sm font-medium bg-accent-primary hover:bg-accent-primary/90 text-white rounded-lg transition-colors shadow-lg shadow-accent-primary/25"
                  >
                    {t('feedUnavailable.goTo')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Timeout/Error overlay */}
          {analysisTimedOut && isAnalyzing && (
            <div className="absolute inset-0 bg-bg-primary/90 backdrop-blur-sm flex items-center justify-center z-20">
              <div className="bg-bg-secondary border border-border-default rounded-xl p-6 max-w-sm text-center shadow-xl">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-warning/20 flex items-center justify-center">
                  <svg className="w-6 h-6 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-2">
                  {t('timeout.title')}
                </h3>
                <p className="text-sm text-text-secondary mb-4">
                  {t('timeout.message')}
                </p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleCancelAnalysis}
                    className="px-4 py-2 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
                  >
                    {t('timeout.cancel')}
                  </button>
                  <button
                    onClick={handleRetry}
                    className="px-4 py-2 text-sm font-medium bg-accent-primary hover:bg-accent-primary/90 text-white rounded-lg transition-colors"
                  >
                    {t('timeout.tryAgain')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Analysis Panel — overlay on mobile (<lg), inline flex sibling on desktop (≥lg).
          lg:min-w-0 on the chart div allows it to shrink so this panel gets its space. */}
      {panelOpen && (
        <>
          {/* Backdrop — mobile/tablet only, closes the drawer on tap */}
          <div
            className="fixed inset-0 bg-black/50 z-[45] lg:hidden"
            onClick={() => setPanelOpen(false)}
          />
          {/* Panel: fixed right drawer on mobile, static flex sibling on desktop */}
          <div
            className="fixed top-16 bottom-0 right-0 z-50 w-full sm:w-80 lg:static lg:top-auto lg:bottom-auto lg:w-80 lg:flex-shrink-0 lg:h-full"
          >
            <AnalysisPanel />
          </div>
        </>
      )}

    </div>

      {/* Analysis history below chart — same-symbol filter on by default */}
      <AnalysisHistoryPanel currentSymbol={symbol} refreshKey={currentAnalysis?.id} />

      {/* Upgrade prompt for plan limits */}
      <UpgradePrompt
        isOpen={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        limitType={t('error.planLimitTitle')}
        message={upgradeMessage}
      />
    </div>
  );
}
